import { memo } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, TooltipContentProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { format } from 'date-fns';
import { cn } from '@/utils/utils.ts';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useOverviewQuery,
  type DateRange,
} from '@/features/dashboard/hooks/queries/get-overview.ts';

const formatValueAndUnit = (inputNumber: number | undefined) => {
  if (inputNumber == null)
    return {
      value: '0',
      unit: 'kWh',
    };

  const isValueBelow1k = inputNumber < 1000;

  const value = isValueBelow1k ? inputNumber.toFixed(2) : (inputNumber / 1000).toFixed(2);
  const unit = isValueBelow1k ? 'kWh' : 'MWh';

  return {
    value,
    unit,
  };
};

const calculateBarsPercentage = ({
  left,
  right,
}: {
  left: number | string | undefined;
  right: number | string | undefined;
}) => {
  const leftNum = Number(left ?? 0);
  const rightNum = Number(right ?? 0);
  if (leftNum === 0 && rightNum === 0) return [50, 50];
  const total = leftNum + rightNum;
  return [Math.round((leftNum / total) * 100), Math.round((rightNum / total) * 100)];
};

const METRIC_COLORS = {
  PV_PRODUCTION: '#18CF87',
  CONSUMED: '#F76354',
  CONSUMED_FROM_GRID: '#EEBA18',
  FED_TO_GRID: '#8DD76B',
  BATTERY_CHARGE: '#038BA8',
  BATTERY_DISCHARGE: '#AC44FB',
} as const;

export const EnergySummaryCards = memo(function EnergySummaryCards({
  dateRange,
  viewContext,
  className,
}: {
  dateRange: DateRange;
  viewContext: string;
  className?: string;
}) {
  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0,
    rootMargin: '20px',
  });

  const { data, isPending, isFetching } = useOverviewQuery({
    dateRange,
    viewContext,
    section: 'overallPerformance',
    enabled: inView,
  });

  const { t } = useTranslation('translation', { keyPrefix: 'metrics' });

  if (isPending) {
    return (
      <div ref={ref} className={cn(className)}>
        <Skeleton className="min-h-24 min-w-72 rounded-lg" />
        <Skeleton className="min-h-24 min-w-72 rounded-lg" />
        <Skeleton className="min-h-24 min-w-72 rounded-lg" />
        <Skeleton className="min-h-24 min-w-72 rounded-lg" />
      </div>
    );
  }

  const overallPerformance = data?.overallPerformance;
  const rangeGranularity = dateRange.rangeGranularity;
  const [yieldBarPercentage, consumedBarPercentage] = calculateBarsPercentage({
    left: overallPerformance?.Yield,
    right: overallPerformance?.Consumed,
  });

  const [fedToGridBarPercentage, consumedFromGridBarPercentage] = calculateBarsPercentage({
    left: overallPerformance?.Fed_to_Grid,
    right: overallPerformance?.Consumed_from_Grid,
  });

  const [batteryChargeBarPercentage, batteryDischargeBarPercentage] = calculateBarsPercentage({
    left: overallPerformance?.Battery_Charge,
    right: overallPerformance?.Battery_Discharge,
  });

  const yieldFmt = formatValueAndUnit(overallPerformance?.Yield);
  const consumedFmt = formatValueAndUnit(overallPerformance?.Consumed);
  const fedToGridFmt = formatValueAndUnit(overallPerformance?.Fed_to_Grid);
  const consumedFromGridFmt = formatValueAndUnit(overallPerformance?.Consumed_from_Grid);
  const batteryChargeFmt = formatValueAndUnit(overallPerformance?.Battery_Charge);
  const batteryDischargeFmt = formatValueAndUnit(overallPerformance?.Battery_Discharge);

  return (
    <div
      ref={ref}
      className={cn(isFetching && 'pointer-events-none animate-pulse opacity-60', className)}
    >
      {/*  Card*/}
      <div className="flex min-h-24 min-w-72 flex-col gap-1 rounded-lg border p-3">
        <div className="flex w-full justify-between">
          <div className="flex flex-col gap-3">
            <span className="text-muted-foreground text-sm">{t('pv_production')}</span>

            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">{yieldFmt.value}</span>
              <span className="text-muted-foreground">{yieldFmt.unit}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3 text-end">
            <span className="text-muted-foreground text-sm">{t('consumption')}</span>

            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">{consumedFmt.value}</span>
              <span className="text-muted-foreground">{consumedFmt.unit}</span>
            </div>
          </div>
        </div>

        <div className="flex w-full">
          <div
            className="h-1.5 rounded-l-lg"
            style={{
              width: `${yieldBarPercentage}%`,
              backgroundColor: METRIC_COLORS.PV_PRODUCTION,
            }}
          ></div>
          <div
            className="h-1.5 rounded-r-lg"
            style={{
              width: `${consumedBarPercentage}%`,
              backgroundColor: METRIC_COLORS.CONSUMED,
            }}
          ></div>
        </div>
      </div>

      {/*  Card*/}
      <div className="flex min-h-24 min-w-72 flex-col gap-1 rounded-lg border p-3">
        <div className="flex w-full justify-between">
          <div className="flex flex-col gap-3">
            <span className="text-muted-foreground text-sm">{t('fed_to_grid')}</span>

            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">{fedToGridFmt.value}</span>
              <span className="text-muted-foreground">{fedToGridFmt.unit}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3 text-end">
            <span className="text-muted-foreground text-sm">{t('consumed_from_grid')}</span>

            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">{consumedFromGridFmt.value}</span>
              <span className="text-muted-foreground">{consumedFromGridFmt.unit}</span>
            </div>
          </div>
        </div>

        <div className="flex w-full">
          <div
            className="h-1.5 w-[60%] rounded-l-lg"
            style={{
              width: `${fedToGridBarPercentage}%`,
              backgroundColor: METRIC_COLORS.FED_TO_GRID,
            }}
          ></div>
          <div
            className="h-1.5 w-[40%] rounded-r-lg"
            style={{
              width: `${consumedFromGridBarPercentage}%`,
              backgroundColor: METRIC_COLORS.CONSUMED_FROM_GRID,
            }}
          ></div>
        </div>
      </div>

      {/*  Card*/}
      <div className="flex min-h-24 min-w-72 flex-col gap-1 rounded-lg border p-3">
        <div className="flex w-full justify-between">
          <div className="flex flex-col gap-3">
            <span className="text-muted-foreground text-sm">{t('battery_charge')}</span>
            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">{batteryChargeFmt.value}</span>
              <span className="text-muted-foreground">{batteryChargeFmt.unit}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3 text-end">
            <span className="text-muted-foreground text-sm">{t('battery_discharge')}</span>
            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">{batteryDischargeFmt.value}</span>
              <span className="text-muted-foreground">{batteryDischargeFmt.unit}</span>
            </div>
          </div>
        </div>

        <div className="flex w-full">
          <div
            className="h-1.5 rounded-l-lg"
            style={{
              width: `${batteryChargeBarPercentage}%`,
              backgroundColor: METRIC_COLORS.BATTERY_CHARGE,
            }}
          ></div>
          <div
            className="h-1.5 rounded-r-lg"
            style={{
              width: `${batteryDischargeBarPercentage}%`,
              backgroundColor: METRIC_COLORS.BATTERY_DISCHARGE,
            }}
          ></div>
        </div>
      </div>

      {/*  Card*/}
      <div className="flex flex-col gap-1 rounded-lg border p-3">
        <div className="flex w-full justify-between gap-2">
          <span className="text-sm text-nowrap text-[#7D7D85]">{t('revenue')}</span>
          <span className="text-xl font-bold text-nowrap">
            {overallPerformance?.Revenue}{' '}
            <span className="text-muted-foreground text-sm">€</span>
          </span>
        </div>
        <ResponsiveContainer width="100%" height={46} debounce={100}>
          <AreaChart
            data={overallPerformance?.revenueChart ?? []}
            margin={{
              top: 0,
              right: -5,
              left: -5,
              bottom: 1,
            }}
          >
            <Tooltip
              content={({ payload }: TooltipContentProps<ValueType, NameType>) => {
                const item = payload?.[0]?.payload;
                if (!item) return null;
                const { value, time } = item;

                let timeFormat = '';
                if (time) {
                  if (rangeGranularity === '5min' || rangeGranularity === 'hourly') {
                    timeFormat = format(time, 'HH:mm');
                  } else {
                    timeFormat = format(time, 'MMM d');
                  }
                }

                return (
                  <div className="flex flex-col gap-2 border-1 border-gray-100 bg-white p-4">
                    <span className="total text-sm">{timeFormat}</span>
                    <span className="text-[#5ebb3c]">{value} €</span>
                  </div>
                );
              }}
            />
            <Area type="monotone" dataKey="value" stroke="#b652ff50" fill="#b652ff90" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});
